import cx from 'classnames';
import { useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';

import { Dropdown } from '../../../../../../design-system/Dropdown/Dropdown';
import { MoreVert } from '../../../../../../design-system/Icons/MoreVert';
import { deleteInstalmentRequest } from '../../../../../../resources/contract/actions';
import { InstalmentViewModel, ContractId } from '../../../../../../resources/contract/types';
import { AllTranslationKeys } from '../../../../../../shared/intl/types';
import { useAppDispatch } from '../../../../../../shared/redux';
import { snakeCaseToCamelCase } from '../../../../../../shared/utilities/snakeCaseToCamelCase';
import styleInstalmentTable from '../../../View/components/ContractInstalmentTable.module.css';

import { DeleteInstalmentModal } from './DeleteInstalmentModal';

import { DuplicateInstalmentModal } from './DuplicateInstalmentModal';
import { EditInstalmentModal } from './EditInstalmentModal';
import s from './PaymentScheduleStep.module.css';

type Props = {
    instalment: InstalmentViewModel;
    index: string;
    currency: string;
    contractId: ContractId;
};

export const ExistingInstalment = ({
    instalment,
    index,
    currency,
    contractId,
}: Props): JSX.Element => {
    const dispatch = useAppDispatch();
    const instalmentStatus = snakeCaseToCamelCase(instalment.status);

    const [isEditInstalmentModalOpened, setIsEditInstalmentModalOpened] = useState(false);
    const [isDuplicateInstalmentModalOpened, setIsDuplicateInstalmentModalOpened] = useState(false);
    const [isDeleteInstalmentModalOpened, setIsDeleteInstalmentModalOpened] = useState(false);
    const [isDropdownOpened, setIsDropdownOpened] = useState(false);

    return (
        <>
            <div className={cx(s.line, s[instalmentStatus])} data-testid="contract-instalment-line">
                <div className={styleInstalmentTable.stateWrapper} data-testid={`${index}.status`}>
                    <span
                        className={cx(
                            styleInstalmentTable.state,
                            styleInstalmentTable[instalmentStatus],
                        )}
                    />
                    <FormattedMessage
                        id={`contract.instalment.status.${instalment.status}` as AllTranslationKeys}
                    />
                </div>
                <div data-testid={`${index}.dueDate`}>
                    <FormattedDate
                        value={instalment.dueDate}
                        day={'2-digit'}
                        month="short"
                        year="numeric"
                    />
                </div>
                <div data-testid={`${index}.payerName`}>{instalment.payerName}</div>
                <div data-testid={`${index}.price.amount`}>
                    <FormattedNumber
                        value={+instalment.price.amount}
                        style="currency"
                        currency={currency}
                        minimumFractionDigits={2}
                        maximumFractionDigits={2}
                    />
                </div>
                <Dropdown
                    buttonClass={s.dropdownButton}
                    dropdownClassName={s.dropdown}
                    menuItemClassName={s.dropdownMenu}
                    isOpened={isDropdownOpened}
                    handleMenu={() => setIsDropdownOpened(!isDropdownOpened)}
                    button={() => (
                        <MoreVert color="var(--ink-light)" data-testid={`${index}.optionsMenu`} />
                    )}
                >
                    {instalmentStatus === 'created' && (
                        <p
                            className={s.dropdownItem}
                            data-testid={`${index}.edit`}
                            onClick={() => {
                                setIsEditInstalmentModalOpened(true);
                            }}
                        >
                            <FormattedMessage id="contract.payment_schedule.instalment.edit" />
                        </p>
                    )}
                    <p
                        className={s.dropdownItem}
                        data-testid={`${index}.duplicate`}
                        onClick={() => {
                            setIsDuplicateInstalmentModalOpened(true);
                        }}
                    >
                        <FormattedMessage id="contract.payment_schedule.instalment.duplicate" />
                    </p>
                    {instalmentStatus === 'created' && (
                        <p
                            className={s.dropdownItem}
                            data-testid={`${index}.delete`}
                            onClick={() => {
                                setIsDeleteInstalmentModalOpened(true);
                            }}
                        >
                            <FormattedMessage id="contract.payment_schedule.instalment.delete.button" />
                        </p>
                    )}
                </Dropdown>
            </div>
            <EditInstalmentModal
                contractId={contractId}
                isOpen={isEditInstalmentModalOpened}
                close={() => setIsEditInstalmentModalOpened(false)}
                instalment={instalment}
            />
            <DuplicateInstalmentModal
                contractId={contractId}
                isOpen={isDuplicateInstalmentModalOpened}
                close={() => setIsDuplicateInstalmentModalOpened(false)}
                currency={instalment.price.currency}
                instalment={instalment}
            />
            <DeleteInstalmentModal
                contractId={contractId}
                isOpen={isDeleteInstalmentModalOpened}
                close={() => setIsDeleteInstalmentModalOpened(false)}
                submit={() => {
                    dispatch(deleteInstalmentRequest(contractId, instalment.id));
                    setIsDeleteInstalmentModalOpened(false);
                }}
                instalmentId={instalment.id}
            />
        </>
    );
};
